import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  GraduationCap, Download, LoaderCircle, Lightbulb, CheckCircle2, ListChecks,
} from "lucide-react";

export default function TutorialView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tutorial");
        setData(data);
      } catch (e) {
        toast.error("No se pudo cargar el tutorial");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/tutorial/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Tutorial_EMAURAN_Presupuesto_Minero.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Tutorial descargado");
    } catch (e) {
      toast.error("No se pudo descargar el PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground text-sm p-10">
        <LoaderCircle className="h-4 w-4 animate-spin" /> Cargando tutorial…
      </div>
    );
  }
  if (!data) return null;
  const { meta, secciones } = data;

  return (
    <div className="flex-1 overflow-y-auto" data-testid="tutorial-view">
      {/* Encabezado */}
      <div className="bg-[#0A1224] text-white px-5 sm:px-8 py-8">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 text-amber-400 text-xs uppercase tracking-widest mb-2">
            <GraduationCap className="h-4 w-4" /> Guía didáctica
          </div>
          <h1 className="font-head font-bold text-2xl sm:text-3xl leading-tight">{meta.titulo}</h1>
          <p className="text-amber-300/90 text-sm mt-1">{meta.subtitulo}</p>
          <p className="text-slate-300 text-sm leading-relaxed mt-4">{meta.intro}</p>
          <Button
            data-testid="tutorial-download-btn"
            onClick={download}
            disabled={downloading}
            className="mt-5 rounded-sm bg-amber-500 hover:bg-amber-600 text-[#0A1224] font-semibold"
          >
            {downloading ? <LoaderCircle className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Descargar tutorial (PDF)
          </Button>
        </div>
      </div>

      {/* Secciones */}
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8 space-y-8">
        {secciones.map((s, idx) => (
          <section key={s.id} data-testid={`tutorial-section-${s.id}`} className="scroll-mt-24">
            <div className="flex items-baseline gap-3">
              <span className="font-head font-bold text-amber-500 text-lg tabular-nums">{String(idx + 1).padStart(2, "0")}</span>
              <h2 className="font-head font-semibold text-xl text-foreground">{s.titulo}</h2>
            </div>
            {s.resumen && <p className="text-sm text-muted-foreground leading-relaxed mt-2">{s.resumen}</p>}

            {s.pasos?.length > 0 && (
              <ul className="mt-3 space-y-2">
                {s.pasos.map((p, i) => (
                  <li key={i} className="flex gap-2.5">
                    <CheckCircle2 className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                    <div>
                      <span className="text-sm font-medium text-foreground">{p.titulo}</span>
                      {p.texto && <span className="text-sm text-muted-foreground"> — {p.texto}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {s.tabla && (
              <div className="mt-4 border border-border rounded-md overflow-hidden">
                {s.tabla.titulo && <div className="px-3 py-2 bg-muted/50 text-xs font-medium text-muted-foreground border-b border-border">{s.tabla.titulo}</div>}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#0A1224] text-white">
                        {s.tabla.headers.map((h, i) => (
                          <th key={i} className={`px-3 py-2 font-medium text-xs ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {s.tabla.rows.map((r, ri) => {
                        const isTotal = ri === s.tabla.rows.length - 1;
                        return (
                          <tr key={ri} className={isTotal ? "bg-amber-500/10 font-semibold" : ri % 2 ? "bg-muted/30" : ""}>
                            {r.map((c, ci) => (
                              <td key={ci} className={`px-3 py-1.5 ${ci === 0 ? "text-left" : "text-right font-mono-num"}`}>{c}</td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {s.tip && (
              <div className="mt-4 flex gap-3 rounded-md border-l-4 border-amber-500 bg-amber-500/10 px-4 py-3">
                <Lightbulb className="h-5 w-5 text-amber-500 shrink-0" />
                <p className="text-sm text-foreground/90"><span className="font-semibold">Consejo del maestro:</span> {s.tip}</p>
              </div>
            )}
          </section>
        ))}

        <div className="flex items-center gap-2 text-xs text-muted-foreground border-t border-border pt-6">
          <ListChecks className="h-4 w-4" />
          ¿Listo para aplicarlo? Empieza en Catálogos → Insumos y sigue el flujo hasta emitir tu reporte.
        </div>
      </div>
    </div>
  );
}
